import { z } from 'zod'

/**
 * THE CONTRACT'S READ SHAPES, TRANSCRIBED.
 *
 * Companion to `query.ts` (which transcribes §9) and governed by the same
 * rule: this file is a *copy* of `api-contracts/types/api/contract.ts` §2, §3,
 * §4, §6 and §7, and any difference from it is a bug in this file.
 * `contract-conformance.test.ts` asserts that mechanically — it reads the real
 * contract off disk and compares, so drift fails a test rather than surfacing
 * as a parse error against a live server months later.
 *
 * WHY TRANSCRIBE AT ALL, RATHER THAN IMPORT
 * The contract pins zod 3.25.76; this console is on zod 4. They never meet —
 * Node resolves the bare `zod` specifier relative to the importing file, so an
 * import would silently bind two incompatible runtimes. The conformance suite
 * is what makes the copy safe.
 *
 * WHAT THIS FILE IS NOT
 * Not the console's internal vocabulary. These are wire shapes: snake_case,
 * five verdicts, array-valued filters, `{data, as_of}` envelopes. The console
 * speaks camelCase with four verdicts. Translation happens in exactly one
 * place (`cadence/mapping.ts`) — importing these types into a component would
 * put the wire format on a screen.
 */

/**
 * ISO 8601 with an offset. The contract narrows rather than using
 * `z.string()` because a timestamp the console cannot parse is a runtime bug
 * the schema could have caught, and both sides render these.
 */
const iso = z.iso.datetime({ offset: true })

/**
 * FIVE VERDICTS. The console's own `runVerdictSchema` has four, and the
 * difference is the single largest divergence between the two vocabularies —
 * pinned as such in the conformance suite. Do not "fix" one to match the
 * other here; `cadence/mapping.ts` is where they meet.
 */
export const contractRunVerdictSchema = z.enum([
  'confirmed',
  'doesnt_match',
  'not_confirmed_yet',
  'needs_you',
  'refused',
])
export type ContractRunVerdict = z.infer<typeof contractRunVerdictSchema>

/**
 * How the customer identity got onto the run — never a claim that it is
 * correct. The prose contract lists two values; the platform emits four and
 * always has, so the executable contract widened rather than forcing the
 * field to lie on the majority of existing runs.
 */
export const contractIdentitySourceSchema = z.enum([
  'intake_link',
  'ask_ceremony',
  'operator_supplied',
  'queued_payload',
])

export const contractRiskLevelSchema = z.enum(['low', 'medium', 'high', 'critical'])

export const contractStepTagSchema = z.enum([
  'prepared',
  'checked',
  'you',
  'claimed',
  'confirmed',
  'refused',
])

/**
 * Every response carries when it was COMPUTED (§8) and WHOSE it is (CAD-008).
 *
 * `tenant_id` landed 2026-08-19 from this console's own request. The lead took
 * it wider than asked — into the envelope rather than onto `/system/health` —
 * so a mislabel is detectable on *any* request rather than only when health
 * happens to be polled. Required, never optional: "the absence is precisely
 * the state we are removing."
 *
 * NOTE: the running server ships this on all 13 envelopes; the executable
 * contract has not caught up. Verified live on six endpoints. Pinned in the
 * conformance suite as a divergence on Cadence's side rather than silently
 * matched to the stale file — see `contract-conformance.test.ts`.
 */
/**
 * HOW CADENCE DECIDED WHICH TENANT TO SERVE — CAD-018's transition field.
 *
 * `deployment-binding` means it fell back to its own `CONSOLE_TENANT_ID`
 * because no `X-Pruvato-Tenant` header arrived. `header` means the caller
 * selected it.
 *
 * This exists so the fallback is never undetectable, which was the whole
 * objection to a header in the first place. The console's assertion gets
 * stronger in two steps: today it checks `tenant_id` matches what it expected;
 * once the switcher sends the header it must also refuse anything that is not
 * `header`, because a silent fall back to the deployment binding would serve a
 * different tenant than the one selected and every screen would look normal.
 *
 * Cadence removes both this field and the fallback when the console starts
 * sending the header — the trigger is ours to pull, not a date.
 */
export const contractTenantSourceSchema = z.enum(['header', 'deployment-binding'])
export type ContractTenantSource = z.infer<typeof contractTenantSourceSchema>

const withAsOf = <T extends z.ZodType>(data: T) =>
  z.object({
    data,
    tenant_id: z.string(),
    tenant_source: contractTenantSourceSchema,
    as_of: iso,
  })

// ── §2: RUNS ───────────────────────────────────────────────────────────────

/**
 * ── WILL APPROVING THIS WRITE — `R-072a`, landed 2026-08-31 ────────────────
 *
 * Two values, and the console asked for exactly two: *"the internal tiers are
 * yours and the console has no business knowing them — if a fifth tier
 * appears, it maps to one of these two before it reaches me"*
 * (`REQ-lighthouse-the-screen-cannot-know-which-mode-this-is`).
 *
 * **It does not carry the founder's four names.** `R-072a` set four
 * operator-facing sentences — *"Practice run. No live data."*, *"Sample data.
 * Not your books."*, *"Read only. Your books, nothing written."*, *"Live.
 * Writes to your books."* — and three of those four collapse into
 * `read_only`. So this answers *"will approving commit to a write"* and cannot
 * answer *"which mode is this"*; the two questions are related and not the
 * same, and `LGH-018` needs both.
 */
export const contractBooksEffectSchema = z.enum(['read_only', 'writes'])
export type ContractBooksEffect = z.infer<typeof contractBooksEffectSchema>

/**
 * `PLT-054`. `LGH-029`'s own REQ argued for a union over a bare
 * `{precision, value}` pair: a single numeric field lets a caller read it
 * without checking `precision` first, which is exactly "prints hours over a
 * days-precision number" — the failure PLT-054's `Done when` names. Two
 * mutually exclusive arms make that a type error here instead of a discipline
 * every render site has to remember.
 *
 * `days` has no producer today — both ends of the clock are always real
 * database timestamps on this platform — kept anyway, the same reasoning the
 * diagnostic pack's `M1ResponseLag` already established: the type should not
 * narrow just because today's one producer happens to always have full
 * precision.
 */
export const contractTurnaroundReadingSchema = z.discriminatedUnion('precision', [
  z.object({ precision: z.literal('hours'), hours: z.number().nonnegative() }).strict(),
  z.object({ precision: z.literal('days'), days: z.number().nonnegative() }).strict(),
])
export type ContractTurnaroundReading = z.infer<typeof contractTurnaroundReadingSchema>

export const contractRunSchema = z
  .object({
    id: z.string(),
    /** Cadence's own identifier, minted before the first attempt. Null until the run mints one. */
    reference: z.string().nullable(),
    /**
     * The provider's identifier, learned only at commit — null on every run
     * that never registered anything and every run that stopped first. This
     * is Zoho's record ID, NOT the QT-000148 style display number a person
     * reads in Zoho's own UI; that third identifier never reaches the
     * journal, and getting it would be a new read against Zoho.
     */
    zoho_reference: z.string().nullable(),
    verdict: contractRunVerdictSchema,
    /** Whether approving this commits to a write. See `contractBooksEffectSchema`. */
    books_effect: contractBooksEffectSchema,
    /**
     * `PLT-054`. Null while `completed_at` is — never a zero, never an
     * average standing in for a case with no honest measurement. See
     * `contractTurnaroundReadingSchema`.
     */
    turnaround: contractTurnaroundReadingSchema.nullable(),
    /** Null when identity was never established — a real and common state, not an error. */
    customer: z.object({ id: z.string(), name: z.string() }).nullable(),
    action_type: z.string(),
    currency: z.string().nullable(),
    /**
     * Null when the run has not priced anything yet, OR when the tenant's own
     * retention policy removed the evidence it was read from. The contract
     * deliberately does not distinguish the two. **Render null as an absence,
     * never as zero** — the contract says so explicitly and leaves the wording
     * of that absence to us.
     */
    amount: z.number().nullable(),
    started_at: iso,
    completed_at: iso.nullable(),
  })
  .strict()
export type ContractRun = z.infer<typeof contractRunSchema>

export const contractRunListResponseSchema = z
  .object({
    data: z.array(contractRunSchema),
    /** EXACT, not an estimate — the pagination UI renders "Showing 1-50 of 247" from it. */
    total: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    tenant_id: z.string(),
    tenant_source: contractTenantSourceSchema,
    as_of: iso,
  })
  .strict()
export type ContractRunListResponse = z.infer<typeof contractRunListResponseSchema>

/**
 * The query string `GET /runs` accepts. Array-valued where the console's own
 * filter is single-valued, and `line_item_text` / `needs` where the console
 * says `lineItemContains` / `amountAbove` — divergences pinned in the
 * conformance suite rather than papered over.
 */
export const contractRunListQuerySchema = z.object({
  verdict: z.array(contractRunVerdictSchema).optional(),
  date_from: iso.optional(),
  date_to: iso.optional(),
  customer: z.string().optional(),
  action_type: z.array(z.string()).optional(),
  rule_fired: z.string().optional(),
  line_item_text: z.string().optional(),
  needs: z.string().optional(),
  sort: z.enum(['time_desc', 'time_asc', 'reference', 'verdict']).default('time_desc'),
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(200).default(50),
})

/**
 * A check, THREE-VALUED. `passed` is derived and null on `unknown` rather
 * than false — folding "we could not tell" into false would put a red mark
 * against a check that never concluded. **Branch on `verdict`, never on
 * `passed`.**
 */
export const contractRuleCheckSchema = z
  .object({
    label: z.string(),
    detail: z.string(),
    verdict: z.enum(['attest', 'refute', 'unknown']),
    passed: z.boolean().nullable(),
  })
  .strict()

export const contractRunDetailSchema = z
  .object({
    id: z.string(),
    reference: z.string().nullable(),
    zoho_reference: z.string().nullable(),
    verdict: contractRunVerdictSchema,
    /** Whether approving this commits to a write. See `contractBooksEffectSchema`. */
    books_effect: contractBooksEffectSchema,
    /** `PLT-054`. Same field, same derivation as `Run.turnaround` — see that declaration. */
    turnaround: contractTurnaroundReadingSchema.nullable(),
    customer: z
      .object({
        id: z.string(),
        name: z.string(),
        identity_source: contractIdentitySourceSchema,
      })
      .nullable(),
    action: z.object({ type: z.string(), risk: contractRiskLevelSchema, timestamp: iso }).strict(),

    rules_applied: z
      .object({ version: z.string(), checks: z.array(contractRuleCheckSchema) })
      .strict(),

    approval: z
      .object({
        approver_id: z.string().nullable(),
        approved_at: iso.nullable(),
        /** The fingerprint the approval is bound to — a version, not the run. */
        version_hash: z.string().nullable(),
        what_was_shown: z.array(z.string()),
      })
      .strict()
      .nullable(),

    /**
     * BOTH CLOCKS, ALWAYS. When it happened and when it was recorded are
     * different facts and the console renders both — a single timestamp hides
     * recording lag, which is where a lot of trouble lives.
     */
    steps: z.array(
      z
        .object({
          n: z.number().int(),
          label: z.string(),
          occurred_at: iso,
          recorded_at: iso,
          tag: contractStepTagSchema,
        })
        .strict(),
    ),

    /** Line-level detail lives ONLY here — the list views and aggregates carry a total at most. */
    line_items: z.array(
      z
        .object({
          item_id: z.string(),
          description: z.string(),
          quantity: z.number(),
          unit_price: z.number().nullable(),
          line_total: z.number().nullable(),
        })
        .strict(),
    ),
    total: z.number().nullable(),
    currency: z.string().nullable(),

    checks: z.array(
      z
        .object({
          statement: z.string(),
          verdict: z.enum(['attest', 'refute', 'unknown']),
          verdict_label: z.string(),
          observed_at: iso,
          detail: z.string(),
        })
        .strict(),
    ),

    provenance: z
      .object({
        complete: z.boolean(),
        line: z.string(),
        missing: z.array(z.string()),
      })
      .strict(),

    caveats: z.array(z.string()),

    receipt: z
      .object({
        /** The W3C VC envelope once the run is terminal; null while in flight. */
        envelope: z.record(z.string(), z.unknown()).nullable(),
        download_url: z.string().nullable(),
      })
      .strict(),

    /**
     * THE FINGERPRINT LIVES HERE, PER ACTION — not once on the run.
     *
     * The console was built around a single run-level fingerprint that every
     * mutation binds to. The contract instead attaches one to each *offered*
     * action, which carries strictly more information: an empty array means
     * there is nothing to do, so the action must be **absent**, not rendered
     * and then rejected. Verified live — a `not_confirmed_yet` run returns
     * `[]`, a `doesnt_match` run returns approve and refuse, and no observed
     * run has offered `rule`.
     */
    next_actions: z.array(
      z
        .object({
          action: z.enum(['approve', 'refuse', 'rule']),
          fingerprint: z.string(),
          description: z.string(),
        })
        .strict(),
    ),
  })
  .strict()
export type ContractRunDetail = z.infer<typeof contractRunDetailSchema>

export const contractRunDetailResponseSchema = withAsOf(contractRunDetailSchema).strict()

// ── §3: AGGREGATES ─────────────────────────────────────────────────────────

const contractMetricSchema = z
  .object({
    value: z.number(),
    trend: z.enum(['up', 'down', 'same']),
    delta: z.number(),
  })
  .strict()

/**
 * A metric that also carries the money behind its count. Only the two
 * count-shaped metrics gain it — a percentage has no total value beside it.
 *
 * `total_value` null means "not every run behind this count could be priced",
 * NOT zero. A metric silently summing only the priced ones would under-report
 * without saying so.
 */
const contractMetricWithValueSchema = contractMetricSchema
  .extend({
    total_value: z.number().nullable(),
    /**
     * THE UNIT, added 2026-08-23 on the console's request, and null carries a
     * second meaning here.
     *
     * Nothing binds a tenant to one currency — it comes from each run's own
     * quote — so null means **these runs do not share one**, and
     * `total_value` is null alongside it, because a total across two
     * currencies is not a smaller number, it is a meaningless one.
     */
    currency: z.string().nullable(),
  })
  .strict()

/**
 * `LGH-031`. A sub-count of `attention_count.value` — how many of the SAME
 * open runs are additionally held by a credit, margin, or discount gate.
 * `runs_7d` reuses the plain `contractMetricWithValueSchema` unchanged; only
 * `attention_count` gains this, per platform-core's own report ("a count
 * with no meaning for every reader of `runs_7d`").
 */
const contractAttentionMetricSchema = contractMetricWithValueSchema.extend({
  policy_held_count: z.number(),
})

export const contractHomeMetricsResponseSchema = withAsOf(
  z
    .object({
      runs_7d: contractMetricWithValueSchema,
      autonomy_rate: contractMetricSchema,
      attention_count: contractAttentionMetricSchema,
      verified_rate: contractMetricSchema,
    })
    .strict(),
).strict()
export type ContractHomeMetricsResponse = z.infer<typeof contractHomeMetricsResponseSchema>

// ── CASE STATUS — `current-state.md` §6.1, `CTR-002` §4 and §A-5 ───────────
//
// **A case status is not a verdict, and the coincidence that both have five
// values is a trap `LGH-001` already caught once.** A verdict is rung 3's
// read-back outcome — did the world agree. A status is the case's own
// disposition — how it ended. They are orthogonal: a run is routinely
// `succeeded` and `not_confirmed_yet` at the same time, which is the normal
// state of everything awaiting verification.

/**
 * THE SIX, AND §6.1 NAMES FIVE. The sixth is proposed, not assumed — see
 * `LGH-013`'s report.
 *
 * `in_flight` covers a case that has started and not finished. §6.1's five are
 * all dispositions: four terminal, one waiting on a person. **None of them
 * describes a case that is simply running**, and `Case.status` currently
 * defaults to `PENDING_ORCHESTRATION`, which is not one of the five — so
 * every case is created in a state the enum as written cannot express.
 *
 * It is proposed here rather than left out because the enum lands **once,
 * with no backfill** (`PLT-004`: the `cases` table is empty), and `DR-007`
 * settled what adding a member later costs: *"once every consumer has been
 * written to handle N, adding N+1 is retrofitting rather than
 * compiler-enforced adaptation."* Getting the member set right is a day now
 * or a migration later.
 */
export const contractCaseStatusSchema = z.enum([
  'in_flight',
  'needs_review',
  'succeeded',
  'succeeded_with_warning',
  'failed',
  'manually_resolved',
])
export type ContractCaseStatus = z.infer<typeof contractCaseStatusSchema>

/**
 * THE LEGAL TRANSITIONS — specified here, **enforced platform-side.**
 *
 * Deliberately not implemented in the console. Whether a transition is legal
 * depends on the case's current state, which is *data*, and `LGH-011`'s
 * standing rule says data is refused at runtime rather than made
 * unrepresentable. A console-side copy of this table would be a second source
 * of truth about the platform's own lifecycle — and the first time the two
 * disagreed, the screen would be the one that was wrong.
 *
 * It is written down because a transition table nobody states is one every
 * reader reconstructs differently.
 *
 *   in_flight     → needs_review | succeeded | succeeded_with_warning | failed
 *   needs_review  → manually_resolved | failed
 *   the other four are TERMINAL.
 *
 * **`needs_review` has exactly two exits, and that is `CTR-002` §A-5 restated
 * rather than a new rule.** A case is in `needs_review` because something is
 * outstanding; answering it is an intervention, so it leaves as
 * `manually_resolved`. A case where the operator only accepted what the
 * drafter already resolved never entered `needs_review` at all.
 *
 * **Terminal is terminal**, because `current-state.md` §5.3 says learning is
 * never retroactive and receipts stand. A case that could re-open would make
 * every receipt provisional.
 */
export const CASE_STATUS_TRANSITIONS: Readonly<
  Record<ContractCaseStatus, readonly ContractCaseStatus[]>
> = {
  in_flight: ['needs_review', 'succeeded', 'succeeded_with_warning', 'failed'],
  needs_review: ['manually_resolved', 'failed'],
  succeeded: [],
  succeeded_with_warning: [],
  failed: [],
  manually_resolved: [],
}

/**
 * `PLT-063`. The exact codes `creditGate.ts`/`marginGate.ts` already produce
 * at the approve-time 409 (`R-050`: one declared vocabulary, not a second
 * hand-typed one for the queue).
 */
export const contractPolicyFlagCodeSchema = z.enum([
  'credit_over_limit',
  'credit_unreadable',
  'margin_below_floor',
  /**
   * `PLT-068`, `REQ-lighthouse-a-third-policy-flag-code-discount-below-floor`.
   * Pulled in the same session it was filed, per the REQ's own invitation —
   * this schema is a hand-maintained enum, not an import of the platform's
   * file, so an unrecognized code would otherwise degrade the item to
   * unreadable rather than fail loudly, which is correct but still a gap
   * worth closing while it's cheap.
   */
  'discount_below_floor',
])
export type ContractPolicyFlagCode = z.infer<typeof contractPolicyFlagCodeSchema>

/**
 * Additive to `reason`, never a replacement for it — `LGH-029` §1's
 * composition ask, proven live by `PLT-063`: a case can carry a
 * `reason` **and** one or more of these, and neither hides the other.
 */
export const contractPolicyFlagSchema = z
  .object({
    code: contractPolicyFlagCodeSchema,
    reason: z.string(),
    /** The underlying attestation's own `freshness.observedAt` — R-075 — never a queue-build-time clock read. */
    as_of: iso,
  })
  .strict()
export type ContractPolicyFlag = z.infer<typeof contractPolicyFlagSchema>

/**
 * `reason` has four values and **`rule_needed` is not one of them**. Per the
 * lead's ruling of 2026-08-19 the contract will not gain a fifth; the console
 * maps `ambiguity` onto its own rule-needed concept, and that mapping lives on
 * our side.
 */
export const contractAttentionItemSchema = z
  .object({
    run_id: z.string(),
    reference: z.string().nullable(),
    urgency: z.enum(['high', 'medium', 'low']),
    reason: z.enum(['approval_needed', 'ambiguity', 'mismatch', 'refused']),
    /**
     * ── `PLT-027` LANDED, SO THIS IS NOW LEGITIMATE — 2026-08-31 ────────────
     *
     * **`LGH-013` tried to add this field and the conformance suite refused
     * within a minute**, correctly: the contract file had no such field, so
     * the transcription would have made the console claim the platform serves
     * something it does not. It was reverted, and the sequence was stated —
     * shapes agreed → `PLT-027` puts it in the contract file → the console
     * transcribes. **That third step is this line.**
     *
     * The suite that refused it then demanded it today: `AttentionItem has the
     * same fields` went red the moment the platform's file gained it.
     *
     * ── AND THE QUESTION `REQ-lighthouse-urgency-and-case-status` §3 ASKED ──
     * Answered in the contract, where it will actually be read:
     *
     *   **`case_status` is authoritative for the lifecycle; `reason` is
     *   authoritative for the ordering.** Neither derives from the other, and
     *   where they appear to disagree they are answering different questions
     *   — a `refused` item is listed so the decision is visible, and its case
     *   may already be terminal. **The screen must not pick one.**
     *
     * Required and non-nullable, which was the §2 ask: `PLT-004` found the
     * `cases` table empty, so no case predates the field and the absent branch
     * would have been written forever for a state that cannot occur.
     */
    case_status: contractCaseStatusSchema,
    title: z.string(),
    detail: z.string(),
    waiting_since: iso,
    action: z.enum(['review', 'rule']),
    amount: z.number().nullable(),
    /**
     * PER ITEM, added 2026-08-23 on the console's request.
     *
     * Per item and not per queue, because the queue may legitimately hold
     * runs quoted in different currencies and a single currency on the
     * response would be wrong the first time it was. Null pairs with
     * `amount: null` — no priced currency for this run.
     */
    currency: z.string().nullable(),
    /** `PLT-063`. `[]` when nothing extra flags this item. See `contractPolicyFlagSchema`. */
    policy_flags: z.array(contractPolicyFlagSchema),
  })
  .strict()
export type ContractAttentionItem = z.infer<typeof contractAttentionItemSchema>

export const contractAttentionQueueResponseSchema = z
  .object({
    data: z.array(contractAttentionItemSchema),
    total: z.number().int().nonnegative(),
    tenant_id: z.string(),
    tenant_source: contractTenantSourceSchema,
    as_of: iso,
  })
  .strict()

/** Same shape as `GET /runs` — a preview, so no `total`. */
export const contractRecentRunsResponseSchema = z
  .object({
    data: z.array(contractRunSchema),
    tenant_id: z.string(),
    tenant_source: contractTenantSourceSchema,
    as_of: iso,
  })
  .strict()

/**
 * How an aggregate's numbers were arrived at. Present from v1 while
 * everything is `declared`, so the day an aggregate starts inferring the
 * console can say so without a schema change — a marker added later would
 * leave every historical response ambiguous about which kind it had been.
 */
export const contractDataConfidenceSchema = z.enum(['declared', 'inferred'])

export const contractHealthWeeklyResponseSchema = withAsOf(
  z
    .object({
      weeks: z.array(
        z
          .object({
            week_start: z.string(),
            runs_count: z.number().int().nonnegative(),
            autonomy_rate: z.number().min(0).max(1),
            attention_average: z.number().nonnegative(),
            verified_rate: z.number().min(0).max(1),
          })
          .strict(),
      ),
      anomalies: z.array(
        z
          .object({
            week_start: z.string(),
            metric: z.enum(['runs_count', 'autonomy_rate', 'attention_average', 'verified_rate']),
            note: z.string(),
          })
          .strict(),
      ),
      data_confidence: contractDataConfidenceSchema,
    })
    .strict(),
).strict()
export type ContractHealthWeeklyResponse = z.infer<typeof contractHealthWeeklyResponseSchema>

export const contractAutonomyBreakdownResponseSchema = withAsOf(
  z
    .object({
      total_runs: z.number().int().nonnegative(),
      fully_autonomous: z.number().int().nonnegative(),
      required_approval: z
        .object({
          count: z.number().int().nonnegative(),
          by_reason: z.array(z.object({ reason: z.string(), count: z.number().int() }).strict()),
        })
        .strict(),
      hit_exception: z
        .object({
          count: z.number().int().nonnegative(),
          by_class: z.array(z.object({ class: z.string(), count: z.number().int() }).strict()),
        })
        .strict(),
      data_confidence: contractDataConfidenceSchema,
    })
    .strict(),
).strict()
export type ContractAutonomyBreakdownResponse = z.infer<
  typeof contractAutonomyBreakdownResponseSchema
>

export const contractVerificationBreakdownResponseSchema = withAsOf(
  z
    .object({
      total_verifications: z.number().int().nonnegative(),
      confirmed: z.number().int().nonnegative(),
      doesnt_match: z
        .object({
          count: z.number().int().nonnegative(),
          most_common_field: z.string().nullable(),
        })
        .strict(),
      not_confirmed_yet: z
        .object({
          count: z.number().int().nonnegative(),
          by_reason: z.array(z.object({ reason: z.string(), count: z.number().int() }).strict()),
        })
        .strict(),
      data_confidence: contractDataConfidenceSchema,
    })
    .strict(),
).strict()
export type ContractVerificationBreakdownResponse = z.infer<
  typeof contractVerificationBreakdownResponseSchema
>

export const contractPolicyGapsResponseSchema = withAsOf(
  z
    .object({
      gaps: z.array(
        z
          .object({
            pattern: z.string(),
            count: z.number().int().positive(),
            example_run_ids: z.array(z.string()).max(5),
            first_seen: iso,
            most_recent: iso,
          })
          .strict(),
      ),
      data_confidence: contractDataConfidenceSchema,
    })
    .strict(),
).strict()
export type ContractPolicyGapsResponse = z.infer<typeof contractPolicyGapsResponseSchema>

// ── §4: MUTATIONS ──────────────────────────────────────────────────────────

/**
 * `approver` is REQUIRED and DECLARED, never inferred.
 *
 * Cadence has no session system — no authenticated identity travels with a
 * console request. Without a name in the body, recording a decision would mean
 * inventing an approver or inferring one from something the caller never
 * asserted, which the contract calls the cheapest possible forgery. The
 * console DOES have a session, so it supplies the signed-in operator's name
 * explicitly rather than letting Cadence guess.
 */
export const contractApproveRequestSchema = z
  .object({
    fingerprint: z.string().min(1),
    approver: z.string().min(1),
    note: z.string().optional(),
  })
  .strict()

export const contractRefuseRequestSchema = z
  .object({
    fingerprint: z.string().min(1),
    reason_class: z.string().min(1),
    approver: z.string().min(1),
    note: z.string().optional(),
  })
  .strict()

/** No `approver`, unlike the other two — the contract does not require one here. */
export const contractRuleRequestSchema = z
  .object({
    fingerprint: z.string().min(1),
    ruling: z
      .object({
        type: z.enum(['customer_identity', 'item_resolution', 'other']),
        value: z.record(z.string(), z.unknown()),
      })
      .strict(),
    note: z.string().optional(),
  })
  .strict()

/**
 * The 409 body. Carries the CURRENT fingerprint and what changed, so the
 * console can re-fetch and let the operator decide again rather than guessing
 * why it was rejected.
 */
export const contractConflictDetailSchema = z
  .object({ current_fingerprint: z.string(), changed: z.string() })
  .strict()

// ── §6: SYSTEM HEALTH ──────────────────────────────────────────────────────

/**
 * `message` is set only when status is not healthy — a message beside a
 * healthy status trains an operator to ignore it.
 */
export const contractSystemStatusSchema = z
  .object({
    status: z.enum(['healthy', 'degraded', 'unreachable']),
    last_checked: iso,
    message: z.string().nullable(),
    affects: z.array(z.string()),
  })
  .strict()

/**
 * Three systems, and **no tenant identifier** — which is why the console
 * cannot confirm whose data it is rendering. Filed as
 * `REQ-console-tenant-identity`; see `cadence/provenance.ts`.
 */
export const contractSystemHealthResponseSchema = withAsOf(
  z
    .object({
      cadence: contractSystemStatusSchema,
      zoho: contractSystemStatusSchema,
      llm: contractSystemStatusSchema,
    })
    .strict(),
).strict()
export type ContractSystemHealthResponse = z.infer<typeof contractSystemHealthResponseSchema>

// ── §7: ERRORS ─────────────────────────────────────────────────────────────

/**
 * Never a stack trace, never an internal identifier. `trace_id` is what to
 * quote when reporting one.
 */
export const contractApiErrorSchema = z
  .object({
    error: z
      .object({
        code: z.string().min(1),
        message: z.string().min(1),
        detail: z.record(z.string(), z.unknown()).optional(),
        trace_id: z.string().min(1),
      })
      .strict(),
  })
  .strict()
export type ContractApiError = z.infer<typeof contractApiErrorSchema>

// ── §11: THE TENANT REGISTRY ───────────────────────────────────────────────

/**
 * Two fields, not one, and they are not redundant.
 *
 * `status` is operational — may this tenant be worked on. `state` is
 * onboarding progress. A tenant can be `ACTIVE` and still `PROSPECT`-stage.
 * Collapsing them loses the difference between "not started yet" and
 * "switched off", which is exactly what a supervisor scanning a cohort needs.
 */
/**
 * WHO CREATED THIS ROW — declared, never inferred.
 *
 * `development` was added 2026-08-23, after `customer | fixture | unknown`,
 * and the reasoning is worth keeping: stamping the Pruvato dev workspace
 * `customer` would make that word mean "a workspace someone uses", and the
 * first time anything counted customers it would be wrong by one.
 *
 * It is also genuinely distinct from `fixture`. A fixture is scaffolding a
 * script created and abandoned; a development workspace is one somebody
 * deliberately keeps, with curated data — which is why it belongs in the
 * switcher and a fixture does not.
 *
 * `unknown` means **nobody recorded it**, never "might be either".
 */
export const contractTenantOriginSchema = z.enum(['customer', 'fixture', 'unknown', 'development'])
export type ContractTenantOrigin = z.infer<typeof contractTenantOriginSchema>

export const contractTenantStatusSchema = z.enum(['PROSPECT', 'ACTIVE', 'INACTIVE'])
export const contractTenantStateSchema = z.enum(['PROSPECT', 'ACTIVATED', 'CONFIGURED'])

export const contractTenantSchema = z
  .object({
    id: z.string(),
    /**
     * FOR HUMANS ONLY — never an identifier. Names get edited and duplicated;
     * anything the console persists keys off the UUID. A saved view keyed on
     * the name breaks on the first rename.
     */
    display_name: z.string(),
    status: contractTenantStatusSchema,
    state: contractTenantStateSchema,
    /**
     * WHO CREATED THIS ROW — declared by whatever made it, never inferred.
     *
     * Added by Cadence 2026-08-23 in answer to the console's own request: the
     * registry was ~98% acceptance fixtures and there was no honest way to tell
     * them from real customers. A name-pattern filter would have been a guess
     * that hid the first real customer called something with "test" in it.
     *
     * `unknown` means **nobody recorded it**, never "might be either". The 384
     * rows that predate the field are `unknown` and were deliberately not
     * classified retrospectively.
     */
    origin: contractTenantOriginSchema,
    created_at: iso,
  })
  .strict()
export type ContractTenant = z.infer<typeof contractTenantSchema>

/**
 * `scope: "platform"` INSTEAD OF `tenant_id`, and the exception is principled.
 *
 * Every other envelope carries `tenant_id` and the console asserts it. These
 * two cannot: a list of all tenants is about no single tenant, and stamping
 * the caller's currently-selected tenant on it would *agree with the session*
 * while the body spanned the platform — the precise mislabel `tenant_id`
 * exists to make detectable.
 *
 * So the console asserts `scope === 'platform'` here instead. The check does
 * not weaken; it changes shape, because the claim being checked changed.
 */
/**
 * Three fields, and **`excluded_fixtures` is not one of them — `LGH-021`.**
 *
 * It was in this helper, optional, so **every** platform-scoped response
 * carried it — including `TenantDetailResponse`, where the contract has no
 * such field. The fake sent it and the console expected it, so the two agreed
 * and neither agreed with the contract. Exactly the review read's failure, in
 * a second place, found by the route-accounting guard on its first run.
 *
 * It belongs on the list alone, which is where the contract puts it and where
 * it is meaningful — see the note below.
 */
const withPlatformScope = <T extends z.ZodType>(data: T) =>
  z.object({
    data,
    as_of: iso,
    scope: z.literal('platform'),
  })

/**
 * `excluded_fixtures` — how many rows Cadence left out of this list.
 *
 * Cadence filters fixtures server-side now, which is the right place for it:
 * it is the registry, and it knows which rows its own scripts created. The
 * console's job is to display the answer, not to second-guess which tenants
 * are real.
 *
 * The count matters as much as the filtering. A list that silently omits rows
 * is a list that cannot be reasoned about — "384 workspaces" reads differently
 * from "384 workspaces, 2 test ones hidden", and only the second lets an
 * operator notice when the number is wrong.
 */
export const contractTenantListResponseSchema = withPlatformScope(z.array(contractTenantSchema))
  .extend({ excluded_fixtures: z.number().int().nonnegative() })
  .strict()
export type ContractTenantListResponse = z.infer<typeof contractTenantListResponseSchema>

export const contractTenantResponseSchema = withPlatformScope(contractTenantSchema).strict()
export type ContractTenantResponse = z.infer<typeof contractTenantResponseSchema>

// ── §5: REFERENCE — the served vocabulary of verdicts ──────────────────────

/**
 * Generated on Cadence's side from the C# `PlainLanguage` table, never retyped.
 * That is the whole reason the console reads them rather than declaring its
 * own: a verdict renamed in the platform reaches the screen without anyone
 * editing a component.
 */
export const contractVerdictReferenceSchema = z
  .object({
    value: contractRunVerdictSchema,
    label: z.string(),
    description: z.string(),
  })
  .strict()

export const contractVerdictsResponseSchema = withAsOf(
  z.array(contractVerdictReferenceSchema),
).strict()
export type ContractVerdictsResponse = z.infer<typeof contractVerdictsResponseSchema>

/**
 * ── LOOSE-ENVELOPE VARIANTS, FOR RUNTIME ONLY ──────────────────────────────
 *
 * Identical to the strict responses above except that the collection is
 * `unknown[]`, so the envelope can be trusted while each element is parsed on
 * its own (`backend/src/cadence/parse-each.ts`).
 *
 * **This does not weaken conformance.** The strict schemas above are what
 * `contract-conformance.test.ts` compares against the contract file, and the
 * elements are still parsed with `contractRunSchema` and
 * `contractAttentionItemSchema` — the very schemas that check. What changes is
 * only whether one bad element takes the good ones with it.
 */
export const contractRunListEnvelopeSchema = z
  .object({
    data: z.array(z.unknown()),
    total: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    tenant_id: z.string(),
    tenant_source: contractTenantSourceSchema,
    as_of: iso,
  })
  .strict()

/**
 * The two envelopes above must carry the same keys as their strict twins, or
 * the loose one silently accepts a response the strict one would have caught.
 * `contract-conformance.test.ts` asserts exactly that, because a pair of
 * schemas that must agree is a pair that can drift.
 */
export const contractAttentionQueueEnvelopeSchema = z
  .object({
    data: z.array(z.unknown()),
    total: z.number().int().nonnegative(),
    tenant_id: z.string(),
    tenant_source: contractTenantSourceSchema,
    as_of: iso,
  })
  .strict()

// ── CTR-002 §V3: THE REVIEW SHAPES ─────────────────────────────────────────
//
// Transcribed from `LGH-010-report.md` §§1–4 and its Addendum, which `CTR-002`
// §V3 names as normative and deliberately does not retype — `charter §10.5`,
// because retyping a schema into prose is how two copies begin. Platform core
// implements the same section, so the day `PLT-011` lands is a conformance run
// rather than an integration.
//
// **v3 replaced v2's arms because v2's could not be served.** `PLT-011` proved
// it by parsing real pack output: a grounded quantity has no catalogue entry,
// and an asked price has no candidates. One root cause — the arms were designed
// for `item` and applied to all three fields.
//
// ── THE ONE RULE THAT NEEDS A MACHINE ──────────────────────────────────────
// **Every arm here, and every arm nested inside one, carries strings only.**
// `current-state.md` §4 requires the class distinction be structural; in C#
// that is a private constructor and a throwing `Match`, neither of which a wire
// format inherits. `PLT-004`'s substitute is the no-numbers rule, because "a
// union whose closure is a convention will leak the first time someone needs to
// sort candidates". `contract-conformance.test.ts` walks this recursively.

/**
 * WHY THE DRAFTER BELIEVES SOMETHING — a union by provenance.
 *
 * `says` is what every provenance owes the operator: a sentence they can read.
 * **Catalogue identity is not universal and does not sit on the base.** That
 * was v2's defect: `catalog_id` was mandatory everywhere, so serving a quantity
 * read from the customer's own text required inventing a catalogue row.
 */
const reviewEvidenceBase = { says: z.string() }
/**
 * `PLT-050`, `R-075`. The instant the catalogue was actually read — pinned at
 * parse time (`ParsePinnedPayload.catalogueReadAt`) and served verbatim on
 * every later read of the same draft, never a fresh clock read at review
 * time. `rfq_span` (the customer's own words) and `operator_decision`
 * (already timed by `decided_at`) carry no `read_at` — there is nothing to
 * have read for either.
 */
const catalogueBacked = { catalog_id: z.string(), catalog_name: z.string(), read_at: iso }

export const contractCatalogEvidenceSchema = z
  .object({ kind: z.literal('catalog'), ...reviewEvidenceBase, ...catalogueBacked })
  .strict()

export const contractTenantVocabularyEvidenceSchema = z
  .object({
    kind: z.literal('tenant_vocabulary'),
    ...reviewEvidenceBase,
    ...catalogueBacked,
    /** ISO, and not orderable as a confidence — which is why it survives the no-numbers rule. */
    taught_at: iso,
  })
  .strict()

export const contractSharedSuggestionEvidenceSchema = z
  .object({ kind: z.literal('shared_suggestion'), ...reviewEvidenceBase, ...catalogueBacked })
  .strict()

/**
 * THE CUSTOMER'S OWN WORDS. No catalogue identity, because there is no
 * catalogue row behind it — `RfqParser.cs:242` grounds a quantity in the literal
 * span of the request, and arguably that is the strongest provenance there is:
 * the customer wrote it, so there is nothing to have got wrong in matching.
 *
 * `span` is NOT `ReviewLine.raw_text`. The line is what the customer wrote;
 * this is the slice one field was read from. See `contractReviewLineSchema`.
 */
export const contractRfqSpanEvidenceSchema = z
  .object({ kind: z.literal('rfq_span'), ...reviewEvidenceBase, span: z.string() })
  .strict()

/** Who settled a field, when. Server-resolved; never a claim the client makes. */
export const contractOperatorRefSchema = z
  .object({
    /** Stable. What the decision package hashes and what answers "who" a year later. */
    id: z.string(),
    /** For humans reading a receipt. Names change; ids do not. */
    name: z.string(),
  })
  .strict()
export type ContractOperatorRef = z.infer<typeof contractOperatorRefSchema>

/**
 * THE OPERATOR SETTLED THIS, ON THIS QUOTE — amendment 5.
 *
 * Distinct from `tenant_vocabulary`, which is *"you taught us this on an
 * earlier quote"*. This is *"you chose this, just now"*, and the two say
 * different things to an operator looking back at a run.
 *
 * It is evidence rather than a fourth `Sourced` class, ruled 2026-08-30: the
 * drafter can never emit an operator-supplied value, so a fourth arm would sit
 * in the drafter's own union describing a state reachable only after a
 * decision. `PLT-019`'s requirement — that a replay can seed rather than
 * re-derive — is carried by `evidence.kind === 'operator_decision'` exactly as
 * well as a class would.
 */
export const contractOperatorDecisionEvidenceSchema = z
  .object({
    kind: z.literal('operator_decision'),
    ...reviewEvidenceBase,
    /** The server's clock. A client-asserted time is as weak as a client-asserted name. */
    decided_at: iso,
    operator: contractOperatorRefSchema,
  })
  .strict()

/**
 * TWO UNIONS, AND THIS IS THE PROPERTY WORTH MOST IN THE CONTRACT.
 *
 * `ResolvedEvidence` omits `shared_suggestion`; `CandidateEvidence` includes
 * it. So a platform that silently resolved a field from anonymous cross-tenant
 * learning **cannot serialise the result**, and `current-state.md` §26 is
 * enforced by a parser rather than by a code review.
 *
 * Adding `rfq_span` and `operator_decision` does not weaken that: the property
 * was never about how many kinds there are, only about **which union can name
 * `shared_suggestion`**. And it is stronger in practice than v2's, because a
 * guarantee whose only cost is a placeholder is a guarantee on its way to being
 * worked around.
 *
 * `operator_decision` is not a candidate: an operator's *past* decision
 * becoming a candidate on a later quote is `tenant_vocabulary`, which is what
 * R-018 builds.
 */
export const contractResolvedEvidenceSchema = z.discriminatedUnion('kind', [
  contractCatalogEvidenceSchema,
  contractTenantVocabularyEvidenceSchema,
  contractRfqSpanEvidenceSchema,
  contractOperatorDecisionEvidenceSchema,
])

export const contractCandidateEvidenceSchema = z.discriminatedUnion('kind', [
  contractCatalogEvidenceSchema,
  contractTenantVocabularyEvidenceSchema,
  contractSharedSuggestionEvidenceSchema,
])

/**
 * One thing the operator could pick. **Order is the platform's ranking** —
 * there is nothing to sort on and there must not be.
 */
export const contractReviewCandidateSchema = z
  .object({
    /** Stable within this draft version. This is what a decision names. */
    candidate_id: z.string(),
    value: z.string(),
    evidence: contractCandidateEvidenceSchema,
  })
  .strict()

const groundedArm = z
  .object({
    kind: z.literal('grounded'),
    /**
     * A STRING FOR EVERY FIELD, including quantity and price. The pack models
     * all three as `string Value`, and the no-numbers rule makes it
     * non-negotiable regardless. **The console must not parse these to render
     * them:** formatting `"40"` as 40 is harmless, formatting `"about 40"` as
     * `NaN` is not, and nothing promises the drafter only grounds clean
     * numerals.
     */
    value: z.string(),
    evidence: contractResolvedEvidenceSchema,
  })
  .strict()

const unmatchedArm = z
  .object({
    kind: z.literal('unmatched'),
    reason: z
      .object({
        /** Groupable. */
        code: z.string(),
        /** The served sentence; the console authors none of it. */
        says: z.string(),
      })
      .strict(),
  })
  .strict()

/** Never a field name — the pack is explicit about this. */
const askedQuestion = { kind: z.literal('asked'), question: z.string() } as const

/**
 * THREE PEERS, AND THE `asked` ARM DIFFERS BY FIELD — amendment 2.
 *
 * An item ask always offers something to choose between; a quantity or price
 * ask has nothing to offer, because there is no catalogue of quantities. v2
 * required `.min(1)` on all three, which made `asked` unserveable for two of
 * them.
 *
 * **`.min(1)` survives where candidates exist, and that is the point.** It was
 * never what stopped the console inferring the class — `kind` being emitted is
 * — but it does mean a platform bug that dropped an item's candidate array is a
 * parse error rather than a picker silently becoming a text box. Removing the
 * field where it cannot apply beats making it optional everywhere.
 */
export const contractItemSourcedSchema = z.discriminatedUnion('kind', [
  groundedArm,
  z
    .object({ ...askedQuestion, candidates: z.array(contractReviewCandidateSchema).min(1) })
    .strict(),
  unmatchedArm,
])

export const contractValueSourcedSchema = z.discriminatedUnion('kind', [
  groundedArm,
  z.object({ ...askedQuestion }).strict(),
  unmatchedArm,
])

export type ContractItemSourced = z.infer<typeof contractItemSourcedSchema>
export type ContractValueSourced = z.infer<typeof contractValueSourcedSchema>
/** Either variant, for code that renders a field without caring which it is. */
export type ContractSourced = ContractItemSourced | ContractValueSourced

/**
 * `raw_text` is the whole line as the customer wrote it — the span all three
 * fields were read from. On an all-`unmatched` line it becomes the row's entire
 * content, which is the second argument for placing it here rather than on each
 * field.
 *
 * It is not the same as `RfqSpanEvidence.span`, which is the slice a particular
 * field came from. There is a good screen in the relationship — highlight which
 * slice each field came from — and `SPEC-LGH-001` requires that highlight to
 * degrade to plain text when the span is not found, rather than erroring.
 */
export const contractReviewLineSchema = z
  .object({
    line_id: z.string(),
    raw_text: z.string(),
    item: contractItemSourcedSchema,
    quantity: contractValueSourcedSchema,
    unit_price: contractValueSourcedSchema,
  })
  .strict()
export type ContractReviewLine = z.infer<typeof contractReviewLineSchema>

/** Per field, so the operator learns which question remains — not which line has one. */
export const contractOutstandingSchema = z
  .object({
    line_id: z.string(),
    field: z.enum(['item', 'quantity', 'unit_price']),
  })
  .strict()
export type ContractOutstanding = z.infer<typeof contractOutstandingSchema>

/**
 * The draft the operator is deciding on.
 *
 * **`GET /runs/:id/review` is not named in `CTR-002`** — V2.2 specifies the
 * write response and the screen plainly needs an initial read, so the shape is
 * the write response's and one parser serves both. Flagged in `LGH-008`'s
 * report rather than assumed: an endpoint the contract did not name is Platform
 * core's to confirm.
 */
/**
 * ── BARE, AND NAMED `resolved_draft` — corrected 2026-08-31, `LGH-021` ─────
 *
 * This was `withAsOf({ lines, … })`, transcribed from `CTR-002` **before the
 * read endpoint existed**. The endpoint shipped today and the contract now
 * declares `ReviewResponse` for it — and it is neither of those things:
 *
 *   • **no envelope.** `RunDetailResponse` is `withAsOf(...)` and the
 *     attention queue carries `tenant_id`/`tenant_source`/`as_of`; this one
 *     carries none of them.
 *   • **`resolved_draft`, not `lines`** — the same field name the write path
 *     uses, deliberately, so a read and a write of the same draft render
 *     through one renderer.
 *
 * **The contract is authoritative and the console was wrong**, so this now
 * matches it. What the missing envelope costs the console is real and is
 * asked about separately — `REQ-lighthouse-the-review-read-has-no-envelope`.
 *
 * **Nothing caught this**, which is the larger finding: the transcription had
 * no counterpart in the contract file to be compared against, so it could say
 * anything. `contract-conformance.test.ts` now drives off the contract's own
 * `ROUTES` map so a shape without a comparison fails rather than passing
 * silently.
 */
export const contractReviewDraftResponseSchema = z
  .object({
    resolved_draft: z.array(contractReviewLineSchema),
    /** What an approval would bind to. Changes whenever a decision is applied. */
    fingerprint: z.string(),
    outstanding: z.array(contractOutstandingSchema),
  })
  .strict()
export type ContractReviewDraftResponse = z.infer<typeof contractReviewDraftResponseSchema>

// ── the write path ─────────────────────────────────────────────────────────

const contractCandidatePickSchema = z
  .object({ kind: z.literal('candidate'), candidate_id: z.string() })
  .strict()
const contractFreeTextSchema = z.object({ kind: z.literal('free_text'), text: z.string() }).strict()
const contractAddToCatalogSchema = z
  .object({ kind: z.literal('add_to_catalog'), text: z.string() })
  .strict()

/**
 * DISCRIMINATED ON `field`, because not every choice is legal for every field.
 *
 * *"Add to catalogue"* is meaningful for `item` and meaningless for `quantity`
 * and `unit_price` — there is no catalogue of quantities. A flat choice union
 * would need a runtime refusal; this makes the mistake unrepresentable, and it
 * is the same correction amendment 2 applied to the read shape.
 */
export const contractReviewDecisionSchema = z.discriminatedUnion('field', [
  z
    .object({
      field: z.literal('item'),
      line_id: z.string(),
      choice: z.discriminatedUnion('kind', [
        contractCandidatePickSchema,
        contractFreeTextSchema,
        contractAddToCatalogSchema,
      ]),
    })
    .strict(),

  /**
   * **NO `CandidatePick` ON EITHER OF THESE — §V3.1.**
   *
   * v3 amendment 2 split `asked` per field, so a quantity or price ask carries
   * no candidates at all. A candidate pick on one could therefore never match
   * anything: it was refused at runtime (422) rather than being impossible to
   * express, which is the shape this contract's own discipline exists to
   * remove. `LGH-006` §3.1 made `add_to_catalog` unrepresentable here for the
   * same reason — *"a flat union would need a runtime refusal; this makes it
   * unrepresentable"* — and the write path simply did not get the same sweep
   * when the read path changed.
   *
   * `FreeText` is what either retains, which is also what a correction needs:
   * an operator fixing a quantity or a price **supplies** a value rather than
   * choosing one.
   */
  z
    .object({
      field: z.literal('quantity'),
      line_id: z.string(),
      choice: z.discriminatedUnion('kind', [contractFreeTextSchema]),
    })
    .strict(),
  z
    .object({
      field: z.literal('unit_price'),
      line_id: z.string(),
      choice: z.discriminatedUnion('kind', [contractFreeTextSchema]),
    })
    .strict(),
])
export type ContractReviewDecision = z.infer<typeof contractReviewDecisionSchema>

/**
 * **`accept_all_known` carries no ids, and that is the safety property.**
 *
 * It means "endorse every `grounded` field on this draft as correctly read",
 * and the platform resolves which those are at `draft_fingerprint`. There is no
 * field in which a client could name something that was not grounded — a field
 * that does not exist cannot carry the mistake, which is stronger than
 * validating a submitted list, because a check can be got wrong once in a
 * refactor and a missing field cannot.
 *
 * **`operator` is on THIS boundary and not on the browser's** — amendment 4.
 * The console's own endpoint takes no operator at all and resolves it from the
 * session; the platform has no session with the browser, so it is told. Per
 * call rather than per decision: *the unit of attribution cannot be finer than
 * the unit of authentication*, and stamping each decision would write one
 * verified identity N times while implying N attributions.
 */
export const contractReviewDecisionsRequestSchema = z
  .object({
    draft_fingerprint: z.string().min(1),
    accept_all_known: z.boolean(),
    decisions: z.array(contractReviewDecisionSchema),
    operator: contractOperatorRefSchema,
  })
  .strict()
export type ContractReviewDecisionsRequest = z.infer<typeof contractReviewDecisionsRequestSchema>

/** Named `resolved_draft` because `CTR-002` V2.2 names it that. */
/** Bare, like the read — `DecisionsResponse` in the contract. See the note on the read. */
export const contractReviewDecisionsResponseSchema = z
  .object({
    resolved_draft: z.array(contractReviewLineSchema),
    fingerprint: z.string(),
    outstanding: z.array(contractOutstandingSchema),
  })
  .strict()
export type ContractReviewDecisionsResponse = z.infer<typeof contractReviewDecisionsResponseSchema>

/**
 * ── THE REVIEW DRAFT'S LOOSE TWINS — `LGH-015` ─────────────────────────────
 *
 * The two above validate `lines` / `resolved_draft` **deeply**, so a single
 * field the console cannot parse fails the whole envelope. At the client that
 * is a thrown `.parse` and a **500** on a screen whose entire design is to
 * degrade one field and keep its neighbours.
 *
 * `LGH-011` built exactly that machinery — `toItemSourced(raw: unknown)` and
 * `toValueSourced(raw: unknown)` `safeParse` each field and fall back to
 * `unreadable`. **Those `unknown` parameters are the tell:** they were written
 * to receive raw wire data, and the strict envelope in front of them meant
 * they only ever received data already proven valid. The degradation could not
 * fire on the read path at all.
 *
 * Found by the conformance run on its first execution against the reserved
 * unknown-class fixture, which answered `500` where it should have rendered
 * two good fields and one honest refusal.
 *
 * Same bargain as the run list and the attention queue: **the envelope is
 * trusted, the elements are not.** Conformance still checks the strict twins,
 * and the key-agreement tests below keep the pairs from drifting.
 */
export const contractReviewDraftEnvelopeSchema = z
  .object({
    resolved_draft: z.array(z.unknown()),
    fingerprint: z.string(),
    outstanding: z.array(contractOutstandingSchema),
  })
  .strict()

/**
 * One line's **structure**, with its three fields left raw.
 *
 * `LGH-011`'s rule, applied one level lower than it was written: *parse the
 * structure, parse the parts, degrade the part.* A line whose `line_id` or
 * `raw_text` is missing is structurally broken and still fails loudly — there
 * is nothing to render and nothing to degrade to. A line whose `unit_price`
 * the console cannot read is a **part**, and the screen already has a word for
 * that.
 */
export const contractReviewLineEnvelopeSchema = z
  .object({
    line_id: z.string(),
    raw_text: z.string(),
    item: z.unknown(),
    quantity: z.unknown(),
    unit_price: z.unknown(),
  })
  .strict()

export const contractReviewDecisionsEnvelopeSchema = z
  .object({
    resolved_draft: z.array(z.unknown()),
    fingerprint: z.string(),
    outstanding: z.array(contractOutstandingSchema),
  })
  .strict()

/**
 * `LGH-035`, `SPEC-ONB-001 §3a/§3b`. Served by `customer-app`, not Cadence's
 * usual snake_case convention — the REQ's own field names were transcribed
 * verbatim by platform-core (`REQ-lighthouse-rfq-source-invites-record-and-
 * endpoints`'s own answer), so this is camelCase on the wire, unlike every
 * other schema in this file. Kept as its own contract layer regardless of
 * that coincidence: parsing here is still what proves the platform sent what
 * it promised, separate from `toRfqSourceInvite`'s translation step.
 */
export const contractRfqSourceRoleSchema = z.enum([
  'staff_admin',
  'staff_standard',
  'external_rfq_only',
])
export type ContractRfqSourceRole = z.infer<typeof contractRfqSourceRoleSchema>

export const contractRfqSourceInviteStatusSchema = z.enum(['pending', 'active', 'revoked'])

export const contractRfqSourceInviteSchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),
    target: z
      .object({
        kind: z.enum(['phone', 'email']),
        value: z.string(),
      })
      .strict(),
    role: contractRfqSourceRoleSchema,
    status: contractRfqSourceInviteStatusSchema,
    invitedByStaffId: z.string(),
    invitedAt: iso,
    confirmedAt: iso.nullable(),
    revokedAt: iso.nullable(),
    revokedByStaffId: z.string().nullable(),
  })
  .strict()
export type ContractRfqSourceInvite = z.infer<typeof contractRfqSourceInviteSchema>

export const contractRfqSourceInviteListSchema = z.array(contractRfqSourceInviteSchema)

/**
 * `LGH-034`, `SPEC-ONB-001`. The first tenant-less record in the platform —
 * settled by Pruvato staff, never scoped by `X-Pruvato-Tenant` (there is no
 * tenant until a human accepts). Camel-cased on the wire like
 * `RfqSourceInvite`, per `REQ-lighthouse-onboarding-request-record-and-
 * endpoints`'s own answer — transcribed from that REQ's prose, not a
 * contract file this repo can read (`customer-app` isn't in Lighthouse's
 * whitelist). `ACCEPTED_NAMED`/`ACCEPTED_MERGED` deliberately never
 * collapse into one `ACCEPTED` — `§2`'s own insistence that the two
 * outcomes carry different authority.
 */
export const contractOnboardingRequestStatusSchema = z.enum([
  'PENDING',
  'ACCEPTED_NAMED',
  'ACCEPTED_MERGED',
  'REJECTED',
])
export type ContractOnboardingRequestStatus = z.infer<typeof contractOnboardingRequestStatusSchema>

export const contractOnboardingRequestChannelSchema = z.enum(['PHONE', 'EMAIL'])

export const contractOnboardingRequestSchema = z
  .object({
    id: z.string(),
    sender: z
      .object({ channel: contractOnboardingRequestChannelSchema, value: z.string() })
      .strict(),
    messageText: z.string(),
    attachment: z.object({ sha256: z.string(), sizeBytes: z.number() }).nullable(),
    qrSourceContext: z.string().nullable(),
    status: contractOnboardingRequestStatusSchema,
    createdAt: iso,
    decidedAt: iso.nullable(),
    decidedByStaffId: z.string().nullable(),
    resultingTenantId: z.string().nullable(),
  })
  .strict()
export type ContractOnboardingRequest = z.infer<typeof contractOnboardingRequestSchema>

export const contractOnboardingRequestListSchema = z.array(contractOnboardingRequestSchema)

/**
 * `LGH-049`, `PLT-105`. Real pasted text from anonymous demo visitors —
 * staff-readable only, no tenant (`crossTenant("demo-submission", ...)`'s own
 * reserved reason). Transcribed from `PLT-105`'s own report prose, not a
 * contract file this repo can read — `customer-app` isn't in Lighthouse's
 * whitelist, same boundary `contractOnboardingRequestSchema` above already
 * crosses the same way.
 *
 * **`shownEvidence` is `z.unknown()`, deliberately** — `1C`'s own write-side
 * report (`REQ-website-demo-submission-labels-needs-a-write-endpoint`) is
 * explicit that this field is opaque, pinned verbatim, and "this contract
 * doesn't get to decide what evidence shapes the demo renders." Replacing
 * that with a guessed shape here would be exactly the kind of invented
 * structure that report's own wording warns against.
 *
 * **`shownScore` is a nullable string**, not a number — the same report
 * states the write side accepts "a plain decimal string or explicit null,"
 * and a value round-tripped through Prisma's `Decimal` is not assumed to
 * become a JSON number on the way out.
 */
export const contractDemoSubmissionLabelSchema = z
  .object({
    lineIndex: z.number(),
    lineText: z.string(),
    disposition: z.enum(['confirmed', 'rejected']),
    shownIdentity: z.string(),
    shownEvidence: z.unknown(),
    shownScore: z.string().nullable(),
  })
  .strict()
export type ContractDemoSubmissionLabel = z.infer<typeof contractDemoSubmissionLabelSchema>

export const contractDemoSubmissionSchema = z
  .object({
    id: z.string(),
    visitorId: z.string(),
    pastedText: z.string(),
    createdAt: iso,
    isRepeatVisitor: z.boolean(),
    labels: z.array(contractDemoSubmissionLabelSchema),
  })
  .strict()
export type ContractDemoSubmission = z.infer<typeof contractDemoSubmissionSchema>

export const contractDemoSubmissionListSchema = z.array(contractDemoSubmissionSchema)
